import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '../context/authContext';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import { useUrlContext } from '../context/urlContext';
import ParentComponent from '../Components/ParentForTableAndFormComponent';
import { errorContext } from '../Components/ErrorDisplay';
import { useConfirm } from '../Components/ConfirmDialog';
import UserForm from '../Forms/UserForm';

function UserManagement() {
  const { UrlHelperObj } = useUrlContext();
  const { handleError, setErrorMessage } = useContext(errorContext);
  const confirm = useConfirm();
  const { user } = useAuth();
  const [userRecords, setUserRecords] = useState([]);
  const [partnerList, setPartnerList] = useState([]);
  const [formShown, setFormShown] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [columnInfo] = useState([
    { title: 'Username', dbname: 'username' },
    { title: 'Role', dbname: 'role_display' },
    { title: 'Club / Partner', dbname: 'club_display' },
    { title: 'Mobile', dbname: 'mobile' },
  ]);

  // Fetch partners list on mount
  useEffect(() => {
    async function fetchData() {
      try {
        let partners = [];
        try {
          const partnerRes = await axios.get(UrlHelperObj.partnerUrl());
          partners = partnerRes.data || [];
        } catch (e) {
          console.warn('Could not fetch partners', e);
        }
        setPartnerList(partners);
        getUserRecords(partners);
      } catch (err) {
        handleError(err);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user records
  async function getUserRecords(partners = partnerList) {
    try {
      const selectedClubId = user?.issuerId || user?.issuer_id;
      const fetchUrl = selectedClubId ? UrlHelperObj.userUrl_GET(selectedClubId) : UrlHelperObj.userUrl();
      const response = await axios.get(fetchUrl);
      
      let rawRecords = [];
      if (Array.isArray(response.data)) {
        rawRecords = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        rawRecords = response.data.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        rawRecords = response.data.users;
      } else if (response.data && Array.isArray(response.data.result)) {
        rawRecords = response.data.result;
      }

      console.log('Fetched raw users:', rawRecords);

      const rolesMap = {
        110: 'Club admin',
        101: 'Club manager',
        102: 'Club Receptionist',
      };

      const processed = rawRecords.map((u) => {
        const partner = partners.find(p => String(p.id) === String(u.issuer_id));
        return {
          ...u,
          // Users have no id from the server; username is the unique key. The
          // table identifies rows by `id`, so use the username as the row id,
          // otherwise every row shares an undefined id and edit/delete hit the
          // wrong user.
          id: u.username,
          role_display: rolesMap[u.role] !== undefined ? rolesMap[u.role] : `Role ${u.role}`,
          club_display: partner ? partner.name : 'None / All Clubs',
        };
      });

      setUserRecords(processed);
      if (processed.length === 0) {
        setErrorMessage('No user records found.');
      } else {
        setErrorMessage(null);
      }
    } catch (err) {
      handleError(err);
    }
  }

  // Delete user record (server deletes by username, ?u=)
  async function deleteUserRecord(id) {
    const ok = await confirm({ title: 'Delete user', message: 'Are you sure you want to delete this user?', confirmText: 'Delete' });
    if (!ok) {
      return;
    }
    const userRecord = userRecords.find(u => String(u.id) === String(id));
    const username = userRecord ? userRecord.username : null;
    if (!username) {
      setErrorMessage('Could not determine the username to delete.');
      return;
    }
    try {
      await axios.delete(UrlHelperObj.userUrlByUsername(username));
      console.log(`Delete of User ${username} successful`);
      setSelectedRow(null);
      getUserRecords();
    } catch (err) {
      handleError(err);
    }
  }

  // Edit user record
  async function editUserRecord(record) {
    try {
      console.log('User edit', JSON.stringify(record));
      await axios.put(UrlHelperObj.userUrl(), record);
      console.log(`Edit of User ${record.username} successful`);
      getUserRecords();
    } catch (err) {
      handleError(err);
    }
  }

  // Add user record
  async function addUserRecord(record) {
    try {
      const payload = { ...record };
      const selectedClubId = user?.issuerId || user?.issuer_id;
      if (selectedClubId) {
        payload.issuer_id = selectedClubId;
      }
      
      console.log('User add', JSON.stringify(payload));
      await axios.post(UrlHelperObj.userUrl(), payload);
      console.log(`Add of User ${payload.username} successful`);
      getUserRecords();
    } catch (err) {
      handleError(err);
    }
  }

  // Submit handler from Form
  function handleFormSubmit(data, isAddOptr, isCancelOptr) {
    setFormShown(false);
    if (isCancelOptr === true) {
      console.log('Cancelled user operation');
      return;
    }

    if (isAddOptr === true) {
      addUserRecord(data);
    } else {
      editUserRecord(data);
    }
  }

  return (
    <Box style={{ padding: 16 }}>
      <Typography variant="h5" style={{ textAlign: 'left', marginBottom: 12 }}>
        User Management
      </Typography>
      <ParentComponent
        data={userRecords}
        columns={columnInfo}
        form={UserForm}
        formTitle="User"
        handleFormSubmitFunc={handleFormSubmit}
        setFormShownFlag={setFormShown}
        deleteFunc={deleteUserRecord}
        setSelectedRow={setSelectedRow}
        redemptionPartnerList={partnerList}
        alreadySelectedOwnRow={selectedRow}
        isAddDisabled={false}
      />
    </Box>
  );
}

export default UserManagement;